import { isBeingWritten } from '@Hooks/Documents';
import { type ApiArchive, EnumArchiveRunState } from '@Model';
import { renderDateTime, type StatusTimelineStep } from '@synqo/frontend-component-library';

/** How far the ZIP download got: asked for, written, and then available, expired or failed. */
// @ai-generated 2026-10-01T12:23:39+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function buildArchiveTimeline(archive: ApiArchive): StatusTimelineStep[] {
    const isScheduled = archive.state === EnumArchiveRunState.Scheduled;
    const isWriting = isBeingWritten(archive);

    return [
        {
            key: 'requested',
            title: 'Angefordert',
            subtitle: renderDateTime(archive.created_at, 'datetime'),
            state: isScheduled ? 'current' : 'done',
            note: isScheduled ? 'Der Download wartet auf einen freien Verarbeitungsplatz.' : undefined,
        },
        {
            key: 'writing',
            title: 'Erstellung',
            subtitle: isScheduled ? 'ausstehend' : undefined,
            state: writingState(isScheduled, isWriting),
            accent: archive.can_rebuild ? 'warning' : 'info',
            note: writingNote(archive, isScheduled, isWriting),
        },
        outcomeStep(archive, isWriting),
    ];
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function writingState(isScheduled: boolean, isWriting: boolean): StatusTimelineStep['state'] {
    if (isScheduled) {
        return 'pending';
    }
    return isWriting ? 'current' : 'done';
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function writingNote(archive: ApiArchive, isScheduled: boolean, isWriting: boolean): string | undefined {
    if (isScheduled || !isWriting) {
        return undefined;
    }
    return archive.can_rebuild ? 'Die Erstellung braucht ungewöhnlich lange.' : 'Der Download wird gerade erstellt.';
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function outcomeStep(archive: ApiArchive, isWriting: boolean): StatusTimelineStep {
    if (archive.state === EnumArchiveRunState.Failed) {
        return { key: 'failed', title: 'Fehlgeschlagen', state: 'failed' };
    }
    if (isWriting) {
        return { key: 'available', title: 'Verfügbar', subtitle: 'ausstehend', state: 'pending' };
    }
    if (archive.expires_at === undefined) {
        return { key: 'available', title: 'Verfügbar', state: 'current', accent: 'success' };
    }

    const expiresAt = renderDateTime(archive.expires_at, 'datetime');
    return !archive.is_expired
        ? { key: 'available', title: 'Verfügbar', subtitle: `bis ${expiresAt}`, state: 'current', accent: 'success' }
        : {
              key: 'expired',
              title: 'Abgelaufen',
              subtitle: `seit ${expiresAt}`,
              state: 'done',
              note: 'Der Download kann mit derselben Auswahl neu erstellt werden.',
          };
}
